import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Eye, Send, Loader2, CheckCircle2, UserRound } from "lucide-react";
import { toast } from "sonner";
import BroadcastHistory from "@/components/admin/BroadcastHistory";

const POLL_MS = 2500;

function usePollBroadcast(onDone) {
  const timer = useRef(null);
  const [job, setJob] = useState(null);
  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/broadcast/${id}`);
        setJob(data);
        if (data.status === "done") onDone(data);
        else poll(id);
      } catch { poll(id); }
    }, POLL_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return { job, start: (d) => { setJob(d); poll(d.id); } };
}

export default function BroadcastEmail() {
  const [groups, setGroups] = useState([]);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [group, setGroup] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [preview, setPreview] = useState({ subject: "", sample: "" });
  const [busy, setBusy] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const { job, start } = usePollBroadcast((d) => {
    toast.success(`Broadcast finished: ${d.sent} email${d.sent === 1 ? "" : "s"} sent${d.failed ? `, ${d.failed} failed` : ""}`);
    setHistoryKey((k) => k + 1);
  });

  useEffect(() => {
    api.get("/admin/broadcast/audiences").then(({ data }) => { setGroups(data.groups); setEmailEnabled(data.email_enabled); }).catch(() => {});
  }, []);

  const selected = groups.find((g) => g.id === group);
  const valid = group && subject.trim().length >= 3 && body.trim().length >= 10;
  const sending = job && job.status !== "done";

  const openPreview = async () => {
    try {
      const { data } = await api.post("/admin/broadcast/preview", { subject, body });
      setPreviewHtml(data.html);
      setPreview({ subject: data.subject, sample: data.sample_name });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed"); }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast/send", { group, subject, body });
      setPreviewHtml("");
      start(data);
      setSubject(""); setBody("");
      setHistoryKey((k) => k + 1);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-6" data-testid="broadcast-email-section">
      <div>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Mail className="w-5 h-5 text-terracotta" /> Broadcast email</h2>
        <p className="text-sm text-foreground/60 mt-1">Write once, send to a whole group. People who unsubscribed or haven't verified their email are skipped automatically.</p>
      </div>

      {job && (
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${sending ? "border-gold/30 bg-gold/5" : "border-emerald-400/30 bg-emerald-500/5"}`} data-testid="broadcast-status">
          {sending ? <Loader2 className="w-5 h-5 animate-spin text-gold" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          <p className="text-sm">
            {sending ? `Sending "${job.subject}" to ${job.group_label}… ${job.sent} of ${job.total} sent` : (
              <span data-testid="broadcast-sent-count"><strong>{job.sent}</strong> email{job.sent === 1 ? "" : "s"} sent to {job.group_label}{job.failed ? ` · ${job.failed} failed` : ""}</span>
            )}
          </p>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-surface p-6 space-y-5">
        {!emailEnabled && <p className="text-sm text-amber-300" data-testid="broadcast-email-disabled">Email sending isn't configured, so broadcasts can't be sent right now.</p>}
        <div>
          <Label>Recipients</Label>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="mt-1.5 bg-ink border-white/10 max-w-md" data-testid="broadcast-group-select"><SelectValue placeholder="Choose a recipient group" /></SelectTrigger>
            <SelectContent>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id} data-testid={`broadcast-group-${g.id}`}>{g.label} · {g.count}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Subject</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} placeholder="e.g. New films this week on Cinevoya" className="mt-1.5 bg-ink border-white/10" data-testid="broadcast-subject-input" />
        </div>
        <div>
          <Label>Message</Label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} maxLength={20000} placeholder={"Write your message…\n\nLeave a blank line between paragraphs. Links like https://cinevoya.com become clickable."} className="mt-1.5 bg-ink border-white/10 leading-relaxed" data-testid="broadcast-body-input" />
          <p className="text-xs text-foreground/45 mt-1">Every email ends with an unsubscribe link.</p>
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-gold/25 bg-gold/5 px-3 py-2.5 text-xs text-foreground/75" data-testid="broadcast-name-note">
            <UserRound className="w-4 h-4 text-gold shrink-0 mt-0.5" />
            <p>
              Type <code className="px-1 rounded bg-ink text-gold">[name]</code> in the subject or message and it's replaced with each recipient's first name ("there" if we don't know it).
              If the message doesn't use <code className="px-1 rounded bg-ink text-gold">[name]</code>, it automatically starts with "Hi [name],".
            </p>
          </div>
        </div>
        <Button onClick={openPreview} disabled={!valid || sending} className="bg-terracotta hover:bg-ember text-white" data-testid="broadcast-preview-btn">
          <Eye className="w-4 h-4" /> Preview email
        </Button>
      </div>

      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml("")}>
        <DialogContent className="bg-surface border-white/10 max-w-3xl" data-testid="broadcast-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">Preview · {preview.subject || subject}</DialogTitle></DialogHeader>
          <p className="text-xs text-foreground/55 -mt-2" data-testid="broadcast-preview-sample-note">Shown as it would look for a recipient named "{preview.sample}". Each person sees their own first name.</p>
          <iframe title="Email preview" srcDoc={previewHtml} sandbox="" className="w-full h-[55vh] rounded-lg border border-white/10 bg-[#0B0D10]" data-testid="broadcast-preview-frame" />
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:items-center">
            <p className="text-sm text-foreground/70 sm:mr-auto" data-testid="broadcast-recipient-count">Will send to <strong>{selected?.count ?? 0}</strong> people · {selected?.label}</p>
            <Button variant="ghost" onClick={() => setPreviewHtml("")} className="border border-white/10" data-testid="broadcast-edit-btn">Keep editing</Button>
            <Button onClick={send} disabled={busy || !emailEnabled || !selected?.count} className="bg-terracotta hover:bg-ember text-white" data-testid="broadcast-send-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {selected?.count ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BroadcastHistory refreshKey={historyKey} />
    </section>
  );
}
